export enum SalesCategoryIcon {
  DESIGN = "DESIGN",
  ENTRY = "ENTRY",
  LIVING = "LIVING",
  KITCHEN = "KITCHEN",
  BED_BATH = "BED_BATH",
  OUTDOOR = "OUTDOOR",
  UTILITY = "UTILITY",
  MISC = "MISC",
  SETTINGS = "SETTINGS",
  OPTIONS = "OPTIONS",
  HOUSE = "HOUSE",
}

/**
 * A sales category is a string, specified in Studio, that encodes how an option is accessed in Showroom.
 *
 *  There are three values with special behaviors (see below):
 *  - UNCATEGORIZED
 *  - OPERATIONS
 *  - EXTRA_OPTIONS
 *
 *  All other standard values are named using the pattern ${PRIMARY_LEVEL}_${SECONDARY_LEVEL}.
 *  For example, BED_BATH_OWNERS_SUITE maps to:
 *  - primary level: "Bed & Bath"
 *  - secondary level: "Owner's Suite"
 */
export enum SalesCategory {
  //===========================================================================
  // Special values
  //===========================================================================

  // Default value for an option.
  //
  // An option with that value will show up under 'MISC_GENERAL' in Showroom. This is a design decision
  // made to allow options to show up in Showroom without an explicit opt-in.
  UNCATEGORIZED = "UNCATEGORIZED",
  // Value to hide option
  OPERATIONS = "OPERATIONS",
  // Extra options are shown in a different view
  EXTRA_OPTIONS = "EXTRA_OPTIONS",

  //===========================================================================
  // Standard values
  //===========================================================================
  DESIGN_ELEVATIONS = "DESIGN_ELEVATIONS",
  DESIGN_PALETTES = "DESIGN_PALETTES",
  DESIGN_FOUNDATION = "DESIGN_FOUNDATION",
  BED_BATH_GENERAL = "BED_BATH_GENERAL",
  BED_BATH_OWNERS_SUITE = "BED_BATH_OWNERS_SUITE",
  BED_BATH_SECONDARY_BATH = "BED_BATH_SECONDARY_BATH",
  BED_BATH_SECONDARY_BED = "BED_BATH_SECONDARY_BED",
  ENTRY_GENERAL = "ENTRY_GENERAL",
  ENTRY_PORCH = "ENTRY_PORCH",
  ENTRY_OWNERS_ENTRY = "ENTRY_OWNERS_ENTRY",
  ENTRY_STAIRS = "ENTRY_STAIRS",
  KITCHEN_GENERAL = "KITCHEN_GENERAL",
  KITCHEN_CABINETS = "KITCHEN_CABINETS",
  KITCHEN_APPLIANCES = "KITCHEN_APPLIANCES",
  LIVING_GENERAL = "LIVING_GENERAL",
  LIVING_FLEXIBLE = "LIVING_FLEXIBLE",
  LIVING_DINING = "LIVING_DINING",
  LIVING_HALL = "LIVING_HALL",
  OUTDOOR_GENERAL = "OUTDOOR_GENERAL",
  OUTDOOR_LIVING = "OUTDOOR_LIVING",
  OUTDOOR_PATIO = "OUTDOOR_PATIO",
  UTILITY_GENERAL = "UTILITY_GENERAL",
  UTILITY_GARAGE = "UTILITY_GARAGE",
  UTILITY_FLEXIBLE = "UTILITY_FLEXIBLE",
  UTILITY_LAUNDRY = "UTILITY_LAUNDRY",
  MISC_GENERAL = "MISC_GENERAL",
}

export type SalesCategoryPrimaryLevel =
  | "BED_BATH"
  | "DESIGN"
  | "ENTRY"
  | "EXTRA_OPTIONS"
  | "KITCHEN"
  | "LIVING"
  | "MISC"
  | "OPERATIONS"
  | "OUTDOOR"
  | "UNCATEGORIZED"
  | "UTILITY";

export type SalesCategorySecondaryLevel =
  | "APPLIANCES"
  | "CABINETS"
  | "DINING"
  | "ELEVATIONS"
  | "FLEXIBLE"
  | "FOUNDATION"
  | "GARAGE"
  | "GENERAL"
  | "HALL"
  | "LAUNDRY"
  | "LIVING"
  | "OWNERS_ENTRY"
  | "OWNERS_SUITE"
  | "PALETTES"
  | "PATIO"
  | "PORCH"
  | "SECONDARY_BATH"
  | "SECONDARY_BED"
  | "STAIRS";

export type SalesCategoryPrimaryLevelIcon = SalesCategoryPrimaryLevel | "GENERIC";
